import { api } from '@connected/api-client';
import { Accordion } from '@connected/ui/components/accordion';
import { Button } from '@connected/ui/components/button';
import { ChevronDownIcon, CloudDownload, CloudOff } from 'lucide-react';
import { useMemo } from 'react';

import { ElectricalProjectFilePreview } from '@/components/electrical-project-file-preview';
import { InformationField } from '@/components/information-field';

import type { ObservationLogEntry } from '../utils/observation-log-types';

import { getLogEntryStatus } from '../utils/observation-log-utils';

export type ObservationLogEntryProps = {
    observationLogEntry: ObservationLogEntry;
    index: number;
    isLast?: boolean;
};

export function ObservationLogEntry({ observationLogEntry, index, isLast = false }: ObservationLogEntryProps) {
    const { label } = getLogEntryStatus(observationLogEntry.NewStatus || null);

    return (
        <Accordion.Item value={observationLogEntry.ElectricalProjectObservationLogId!} className="border-b-0 relative pl-12 pb-6">
            <div className="absolute left-0 top-0 bottom-0 flex flex-col items-center w-10">
                <div className="flex bg-background-secondary px-4 py-2 rounded-full justify-center items-center shrink-0">
                    {index + 1}
                </div>
                {!isLast && (
                    <div className="w-px bg-border-primary flex-1 mt-2" />
                )}
            </div>

            <Accordion.Trigger className="flex justify-start gap-2 p-0" showTrigger={false}>
                <div className="flex flex-col">
                    <div className="flex flex-row gap-2">
                        <p className="text-text-secondary text-sm font-semibold truncate">
                            {label}
                        </p>
                        <ChevronDownIcon className="text-muted-foreground pointer-events-none size-4 shrink-0 translate-y-0.5 transition-transform duration-200 [[data-state=open]_&]:rotate-180" />
                    </div>
                    <div className="flex flex-row gap-2">
                        <p className="text-sm text-text-quaternary font-normal">
                            {observationLogEntry.CreateDate && `${new Date(observationLogEntry.CreateDate).toLocaleDateString()} ${new Date(observationLogEntry.CreateDate).toLocaleTimeString()}`}
                        </p>
                        <p className="text-sm text-text-quaternary font-normal">
                            {' • '}
                        </p>
                        <p className="text-sm text-text-quaternary font-normal">
                            {observationLogEntry.User?.Name}
                        </p>
                        <p className="text-sm text-text-quaternary font-normal">
                            {' • '}
                        </p>
                        <p className="text-sm text-text-quaternary font-normal">
                            {observationLogEntry.User?.OrganizationName || 'ADMIN'}
                        </p>

                    </div>
                </div>
            </Accordion.Trigger>
            <Accordion.Content className="pb-0 pt-4 flex flex-col gap-4">
                <InformationField label="Omschrijving" value={observationLogEntry.Message} />
                <div>
                    <p className="font-semibold text-text-tertiary text-sm">Bestanden</p>
                    {observationLogEntry.Files && observationLogEntry.Files?.filter(x => !x.File?.IsImage).length > 0
                        ? (
                                <div className="mt-2 flex flex-col gap-2 p-1">
                                    {observationLogEntry.Files.filter(x => !x.File?.IsImage).map(file => (
                                        <div key={file.ElectricalProjectFileId} className="flex items-center justify-between gap-1">
                                            <ElectricalProjectFilePreview
                                                electricalProjectFileId={file.ElectricalProjectFileId!}
                                            />
                                            <DownloadButton
                                                fileId={file.ElectricalProjectFileId}
                                                fileName={file.File?.Name}
                                                fileExtension={file.File!.ContentType!}
                                            />
                                        </div>
                                    ))}
                                </div>
                            )
                        : (
                                <p className="text-text-secondary text-sm italic mt-2">Er zijn geen bestanden toegevoegd aan dit logboek item.</p>
                            )}
                </div>
            </Accordion.Content>
        </Accordion.Item>
    );
}

function DownloadButton({ fileId, disabled = false, fileName, fileExtension }: { fileId: string | null | undefined; disabled?: boolean; fileName?: string | null | undefined; fileExtension?: string | undefined }) {
    const query = api.useQuery('get', '/api/v2/electrical/projects/files/download', {
        params: {
            query: {
                ElectricalProjectFileId: fileId ?? '',
            },
        },
        parseAs: 'blob',
    });

    const url = useMemo(() => {
        if (!fileId || !query.data) {
            return null;
        }
        const blob = new Blob([query.data], { type: fileExtension });
        const url = URL.createObjectURL(blob);
        return url;
    }, [fileId, query.data, fileExtension]);

    return (

        <Button
            variant="outline"
            size="sm"
            className="h-14 justify-start p-2"
            disabled={disabled || !fileId || !url}
            asChild={!!url}
        >
            {url
                ? <a href={url} download={fileName}><CloudDownload className="w-4 h-4" /></a>
                : <CloudOff className="w-4 h-4" />}
        </Button>
    );
}
